// Post-processing for the HD-2D diorama look: depth of field (strong toward
// the top and bottom of the frame, like a tilt-shift miniature), bloom for the
// plankton and lanterns, a night colour grade, vignette and fine grain.
import * as THREE from 'three';

const quadVS = /* glsl */`
varying vec2 vUv;
void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`;

function pass(fragmentShader, uniforms) {
  return new THREE.ShaderMaterial({ vertexShader: quadVS, fragmentShader, uniforms, depthTest: false, depthWrite: false });
}

const dofFS = /* glsl */`
uniform sampler2D tColor; uniform sampler2D tDepth;
uniform vec2 texel; uniform float near; uniform float far;
uniform float focus; uniform float range; uniform float spread; uniform float maxBlur; uniform float tilt;
varying vec2 vUv;
float viewZ(float d) { return (near * far) / (far - d * (far - near)); }
float coc(vec2 uv) {
  float z = viewZ(texture2D(tDepth, uv).x);
  float c = clamp((abs(z - focus) - range) / spread, 0.0, 1.0);
  float band = smoothstep(0.30, 0.52, abs(uv.y - 0.47)) * tilt;   // miniature falloff
  return clamp(max(c, band), 0.0, 1.0);
}
void main() {
  float c0 = coc(vUv);
  vec4 sum = vec4(texture2D(tColor, vUv).rgb, 1.0);
  if (c0 > 0.02) {
    const float GA = 2.39996323;
    for (int i = 1; i < 24; i++) {
      float r = sqrt(float(i) / 24.0);
      vec2 o = vec2(cos(float(i) * GA), sin(float(i) * GA)) * r * maxBlur * c0 * texel;
      float cs = coc(vUv + o);
      float w = smoothstep(0.0, 0.25, cs) + 0.15;   // sharp pixels don't bleed into blur
      sum += vec4(texture2D(tColor, vUv + o).rgb * w, w);
    }
  }
  gl_FragColor = vec4(sum.rgb / sum.a, 1.0);
}`;

const brightFS = /* glsl */`
uniform sampler2D tColor; uniform float threshold; varying vec2 vUv;
void main() {
  vec3 c = texture2D(tColor, vUv).rgb;
  float l = max(c.r, max(c.g, c.b));
  float k = smoothstep(threshold, threshold + 0.6, l);
  gl_FragColor = vec4(c * k, 1.0);
}`;

// Dual-filter (Kawase) down/up sampling.
const downFS = /* glsl */`
uniform sampler2D tSrc; uniform vec2 texel; varying vec2 vUv;
void main() {
  vec3 c = texture2D(tSrc, vUv).rgb * 4.0;
  c += texture2D(tSrc, vUv + vec2(-1.0, -1.0) * texel).rgb;
  c += texture2D(tSrc, vUv + vec2( 1.0, -1.0) * texel).rgb;
  c += texture2D(tSrc, vUv + vec2(-1.0,  1.0) * texel).rgb;
  c += texture2D(tSrc, vUv + vec2( 1.0,  1.0) * texel).rgb;
  gl_FragColor = vec4(c / 8.0, 1.0);
}`;
const upFS = /* glsl */`
uniform sampler2D tSrc; uniform sampler2D tBase; uniform vec2 texel; varying vec2 vUv;
void main() {
  vec3 c = vec3(0.0);
  c += texture2D(tSrc, vUv + vec2(-2.0, 0.0) * texel).rgb;
  c += texture2D(tSrc, vUv + vec2( 2.0, 0.0) * texel).rgb;
  c += texture2D(tSrc, vUv + vec2(0.0, -2.0) * texel).rgb;
  c += texture2D(tSrc, vUv + vec2(0.0,  2.0) * texel).rgb;
  c += texture2D(tSrc, vUv + vec2(-1.0, -1.0) * texel).rgb * 2.0;
  c += texture2D(tSrc, vUv + vec2( 1.0, -1.0) * texel).rgb * 2.0;
  c += texture2D(tSrc, vUv + vec2(-1.0,  1.0) * texel).rgb * 2.0;
  c += texture2D(tSrc, vUv + vec2( 1.0,  1.0) * texel).rgb * 2.0;
  gl_FragColor = vec4(c / 12.0 + texture2D(tBase, vUv).rgb, 1.0);
}`;

const finalFS = /* glsl */`
uniform sampler2D tColor; uniform sampler2D tBloom;
uniform float bloom; uniform float exposure; uniform float time; uniform float vignette; uniform float grain;
uniform vec3 shadowTint; uniform vec3 highTint; uniform float saturation; uniform float fade; uniform float paper; uniform vec2 res;
varying vec2 vUv;
vec3 aces(vec3 x) { return clamp((x * (2.51 * x + 0.03)) / (x * (2.43 * x + 0.59) + 0.14), 0.0, 1.0); }
float hash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
void main() {
  vec3 c = texture2D(tColor, vUv).rgb + texture2D(tBloom, vUv).rgb * bloom;
  c = aces(c * exposure);
  float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
  c = mix(vec3(l), c, saturation);
  c *= mix(shadowTint, highTint, smoothstep(0.05, 0.75, l));
  vec2 d = vUv - 0.5;
  c *= 1.0 - vignette * smoothstep(0.2, 0.85, dot(d, d) * 2.2);
  c = pow(c, vec3(1.0 / 2.2));
  if (paper > 0.0) {
    // Washi: long fibres and a fixed tooth, multiplied in so blacks stay black.
    vec2 px = vUv * res;
    float fibre = hash(floor(px / vec2(9.0, 2.0)) + floor(px.y / 37.0)) * hash(floor(px / vec2(2.0, 11.0)));
    float tooth = hash(floor(px / 2.0));
    c *= 1.0 - paper * (0.06 * tooth + 0.07 * fibre);
    c += paper * vec3(0.012, 0.011, 0.02) * fibre;
  }
  c += (hash(vUv * 917.0 + time) - 0.5) * grain;
  c *= fade;
  gl_FragColor = vec4(c, 1.0);
}`;

export class Post {
  constructor(renderer) {
    this.renderer = renderer;
    this.scene = new THREE.Scene();
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this.quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2));
    this.scene.add(this.quad);
    const rt = (w, h, extra = {}) => new THREE.WebGLRenderTarget(w, h, { type: THREE.HalfFloatType, ...extra });
    this.makeTargets = (w, h) => {
      this.main?.dispose(); this.dof?.dispose(); this.mips?.forEach(m => m.dispose());
      this.main = rt(w, h, { samples: 4, depthTexture: new THREE.DepthTexture(w, h) });
      this.dof = rt(w, h);
      this.mips = [];
      let mw = w >> 1, mh = h >> 1;
      for (let i = 0; i < 5; i++) { this.mips.push(rt(Math.max(1, mw), Math.max(1, mh))); mw >>= 1; mh >>= 1; }
      this.ups = this.mips.map(m => rt(m.width, m.height));
    };
    this.dofMat = pass(dofFS, {
      tColor: { value: null }, tDepth: { value: null }, texel: { value: new THREE.Vector2() },
      near: { value: 0.1 }, far: { value: 100 }, focus: { value: 30 }, range: { value: 6 }, spread: { value: 18 },
      maxBlur: { value: 6 }, tilt: { value: 0.65 },
    });
    this.brightMat = pass(brightFS, { tColor: { value: null }, threshold: { value: 0.75 } });
    this.downMat = pass(downFS, { tSrc: { value: null }, texel: { value: new THREE.Vector2() } });
    this.upMat = pass(upFS, { tSrc: { value: null }, tBase: { value: null }, texel: { value: new THREE.Vector2() } });
    this.finalMat = pass(finalFS, {
      tColor: { value: null }, tBloom: { value: null }, bloom: { value: 0.9 }, exposure: { value: 1.15 },
      time: { value: 0 }, vignette: { value: 0.55 }, grain: { value: 0.025 },
      shadowTint: { value: new THREE.Color(0.82, 0.9, 1.12) }, highTint: { value: new THREE.Color(1.06, 1.0, 0.92) },
      saturation: { value: 1.08 }, fade: { value: 1 }, paper: { value: 0 }, res: { value: new THREE.Vector2(1, 1) },
    });
    this.settings = { dof: true, bloom: true };
  }

  setSize(w, h) { this.w = w; this.h = h; this.makeTargets(w, h); this.finalMat?.uniforms.res.value.set(w, h); }

  draw(mat, target) {
    this.quad.material = mat;
    this.renderer.setRenderTarget(target);
    this.renderer.render(this.scene, this.camera);
  }

  render(scene, camera, look, time) {
    const r = this.renderer;
    r.setRenderTarget(this.main);
    r.render(scene, camera);
    let color = this.main.texture;
    if (this.settings.dof) {
      const u = this.dofMat.uniforms;
      u.tColor.value = this.main.texture; u.tDepth.value = this.main.depthTexture;
      u.texel.value.set(1 / this.w, 1 / this.h);
      u.near.value = camera.near; u.far.value = camera.far;
      u.focus.value = look.focus; u.range.value = look.range; u.spread.value = look.spread;
      u.maxBlur.value = look.maxBlur * (this.h / 900); u.tilt.value = look.tilt;
      this.draw(this.dofMat, this.dof);
      color = this.dof.texture;
    }
    // Bloom chain.
    this.brightMat.uniforms.tColor.value = color;
    this.brightMat.uniforms.threshold.value = look.threshold;
    this.draw(this.brightMat, this.mips[0]);
    for (let i = 1; i < this.mips.length; i++) {
      this.downMat.uniforms.tSrc.value = this.mips[i - 1].texture;
      this.downMat.uniforms.texel.value.set(0.5 / this.mips[i - 1].width, 0.5 / this.mips[i - 1].height);
      this.draw(this.downMat, this.mips[i]);
    }
    let up = this.mips[this.mips.length - 1];
    for (let i = this.mips.length - 2; i >= 0; i--) {
      this.upMat.uniforms.tSrc.value = up.texture;
      this.upMat.uniforms.tBase.value = this.mips[i].texture;
      this.upMat.uniforms.texel.value.set(0.5 / up.width, 0.5 / up.height);
      this.draw(this.upMat, this.ups[i]);
      up = this.ups[i];
    }
    const f = this.finalMat.uniforms;
    f.tColor.value = color; f.tBloom.value = up.texture;
    f.bloom.value = this.settings.bloom ? look.bloom : 0;
    f.exposure.value = look.exposure; f.time.value = time % 100;
    f.vignette.value = look.vignette; f.fade.value = look.fade ?? 1;
    this.draw(this.finalMat, null);
  }
}
